import React, { useState, useEffect, useContext, useCallback } from "react";
import { UserContext } from "../../../contexts/CurrentUserContext";
import { getAllGroups, getUsersSummary } from "../../../api/api";
import { getAllReports } from "../../../api/reportsApi";
import {
  getTimesheetApprovalsByApprover,
  getAllTimesheetApprovals,
  updateTimesheetStep,
  getStatusText,
  getStatusColor,
  TIMESHEET_STATUSES,
} from "../../../api/timesheetApi";
import TimesheetNotification from "./TimesheetNotification";
import "./TimesheetApproval.css";
import "../../Report/shared/timesheetTableCells.css";
import Preloader from "../../Preloader/Preloader";
import { getCalendar } from "../../../api/api";
import { formatHoursWithRounding } from "../../../utils/timeUtils";
import { filterTimesheetVisibleReports } from "../../../utils/timesheetReportUtils";
import { usePendingTimesheets } from "../../../contexts/PendingTimesheetsContext";
import {
  TIMESHEET_BUSINESS_TRIP_IDS,
  TIMESHEET_SPECIAL_JOB_TYPES,
  TimesheetDayCellContent,
  createTimesheetCellHelpers,
  hasTimesheetStatusColoring,
} from "../../Report/shared/timesheetTableRendering";

const getCurrentPeriodLabel = () =>
  new Date().toLocaleDateString("ru", { month: "long", year: "numeric" });

const TimesheetApproval = () => {
  const { userData } = useContext(UserContext);
  const { updatePendingCount } = usePendingTimesheets();
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [departmentUsers, setDepartmentUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [allVisibleReports, setAllVisibleReports] = useState([]);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(true);
  const [calendarData, setCalendarData] = useState({});
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [isLoadingTable, setIsLoadingTable] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [selectedApprovalRequest, setSelectedApprovalRequest] = useState(null);
  const [notification, setNotification] = useState({
    isVisible: false,
    type: "success",
    title: "",
    message: "",
  });
  const [expandedMultiDeptUsers, setExpandedMultiDeptUsers] = useState(
    () => new Set(),
  );

  // Фильтры
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [periodFilter, setPeriodFilter] = useState(getCurrentPeriodLabel);

  const toggleMultiDeptExpand = useCallback((userId) => {
    setExpandedMultiDeptUsers((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    setExpandedMultiDeptUsers(new Set());
  }, [selectedMonth, selectedDepartment]);

  // Функции для фильтрации
  const getUniqueDepartments = useCallback(() => {
    const departments = [
      ...new Set(pendingApprovals.map((approval) => approval.department)),
    ];
    return departments.sort();
  }, [pendingApprovals]);

  const getUniquePeriods = useCallback(() => {
    const periods = [
      ...new Set(pendingApprovals.map((approval) => approval.date)),
    ];
    const currentPeriod = getCurrentPeriodLabel();
    if (!periods.includes(currentPeriod)) {
      periods.push(currentPeriod);
    }
    return periods.sort();
  }, [pendingApprovals]);

  const getFilteredApprovals = useCallback(() => {
    return pendingApprovals.filter((approval) => {
      const matchesDepartment =
        !departmentFilter || approval.department === departmentFilter;
      const matchesPeriod = !periodFilter || approval.date === periodFilter;
      return matchesDepartment && matchesPeriod;
    });
  }, [pendingApprovals, departmentFilter, periodFilter]);

  // Функции для работы с фильтрами
  const handleDepartmentFilterChange = (e) => {
    setDepartmentFilter(e.target.value);
  };

  const handlePeriodFilterChange = (e) => {
    setPeriodFilter(e.target.value);
  };

  const clearFilters = () => {
    setDepartmentFilter("");
    setPeriodFilter("");
  };

  const filterReportsBySelectedDepartment = useCallback(
    (reportsList) =>
      (reportsList || []).filter(
        (report) => report?.group?.id === selectedDepartment?.id,
      ),
    [selectedDepartment],
  );

  // Функция для проверки доступа к просмотру запросов на согласование
  const canViewApprovalRequests = useCallback(() => {
    if (!userData) return false;

    // Доступ только для АДМС и согласующих пользователей (ID 275 и 131)
    return (
      userData.access_levels.some((level) => level.name === "АДМС") ||
      userData.id === 275 ||
      userData.id === 131
    );
  }, [userData]);

  // Функция для загрузки запросов на согласование
  const loadApprovalRequests = useCallback(async () => {
    if (!userData?.id) {
      console.log(
        "Нет данных пользователя для загрузки запросов на согласование",
      );
      return;
    }

    try {
      // Определяем, какой API использовать в зависимости от уровня доступа
      const isAdmin = userData.access_levels?.some(
        (level) => level.name === "АДМС",
      );

      console.log(
        `Загружаем запросы на согласование для пользователя: ${userData.id}, АДМС: ${isAdmin}`,
      );

      // Если пользователь АДМС - загружаем все табели, иначе только свои
      const response = isAdmin
        ? await getAllTimesheetApprovals()
        : await getTimesheetApprovalsByApprover(userData.id);

      // Преобразуем данные из API в формат, ожидаемый компонентом
      const transformedApprovals = response.map((approval) => ({
        id: approval.id,
        departmentId: approval.group.id,
        department: approval.group.name,
        date: `${new Date(approval.year, approval.month - 1).toLocaleDateString(
          "ru",
          { month: "long", year: "numeric" },
        )}`,
        month: `${approval.year}-${approval.month.toString().padStart(2, "0")}`,
        status: approval.status,
        submissionDate: new Date(approval.created_at).toLocaleDateString("ru"),
        submittedBy: `${
          approval.initiator.surname
        } ${approval.initiator.name.charAt(0)}.`,
        recipients: approval.steps.map(
          (step) => `${step.approver.surname} ${step.approver.name.charAt(0)}.`,
        ),
        approvers: approval.steps.map((step) => ({
          id: step.id,
          name: `${step.approver.surname} ${step.approver.name.charAt(0)}.`,
          status: step.status,
          comment: step.comment,
          decidedAt: step.decided_at,
        })),
        // Сохраняем оригинальные данные для API вызовов
        originalData: approval,
      }));

      console.log(
        "Преобразованные данные запросов на согласование:",
        transformedApprovals,
      );
      setPendingApprovals(transformedApprovals);
    } catch (error) {
      console.error("Ошибка при загрузке запросов на согласование:", error);
      // В случае ошибки показываем пустой список
      setPendingApprovals([]);
    }
  }, [userData?.id, userData?.access_levels]);

  // Загружаем отделы и запросы на согласование
  useEffect(() => {
    if (userData && canViewApprovalRequests()) {
      const loadAllDepartments = async () => {
        try {
          setIsLoadingDepartments(true);
          const allGroups = await getAllGroups();
          const departments = allGroups.filter((group) => group.is_active);
          setAvailableDepartments(departments);
          setIsLoadingDepartments(false);
        } catch (error) {
          console.error("Ошибка при загрузке отделов:", error);
          setIsLoadingDepartments(false);
        }
      };

      loadAllDepartments();
      loadApprovalRequests();
    }
  }, [userData, canViewApprovalRequests, loadApprovalRequests]);

  // Загружаем пользователей и отчеты для выбранного отдела и месяца
  useEffect(() => {
    const loadDepartmentUsers = async () => {
      if (!selectedDepartment) {
        setIsLoadingTable(false);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoadingTable(true);
        setIsLoading(true);

        const response = await getUsersSummary({
          groupId: selectedDepartment.id,
          isActive: true,
          pageSize: 100,
        });

        const usersInDepartment = response.users;
        setDepartmentUsers(usersInDepartment);

        const firstDayOfMonth = new Date(
          selectedMonth.getFullYear(),
          selectedMonth.getMonth(),
          1,
        )
          .toISOString()
          .split("T")[0];

        const allReports = await Promise.all(
          usersInDepartment.map(async (user) => {
            const userReports = await getAllReports(user.id, firstDayOfMonth);
            return userReports;
          }),
        );

        const flattenedReports = allReports.flat();
        const visibleReports = filterTimesheetVisibleReports(flattenedReports);
        const departmentOnlyReports =
          filterReportsBySelectedDepartment(visibleReports);
        setAllVisibleReports(visibleReports);
        setReports(departmentOnlyReports);
      } catch (error) {
        console.error("Ошибка при загрузке данных:", error);
      } finally {
        setIsLoadingTable(false);
        setIsLoading(false);
      }
    };

    loadDepartmentUsers();
  }, [selectedDepartment, selectedMonth, filterReportsBySelectedDepartment]);

  // Загружаем календарь
  useEffect(() => {
    const loadCalendarData = async () => {
      try {
        const firstDay = new Date(
          Date.UTC(
            selectedMonth.getFullYear(),
            selectedMonth.getMonth(),
            1,
            12,
            0,
            0,
            0,
          ),
        );
        const lastDay = new Date(
          Date.UTC(
            selectedMonth.getFullYear(),
            selectedMonth.getMonth() + 1,
            0,
            12,
            0,
            0,
            0,
          ),
        );

        const calendar = await getCalendar(
          firstDay.toISOString().split("T")[0],
          lastDay.toISOString().split("T")[0],
        );
        const calendarMap = {};
        calendar.forEach((day) => {
          calendarMap[day.date] = day;
        });
        setCalendarData(calendarMap);
      } catch (error) {
        console.error("Ошибка при загрузке календаря:", error);
      }
    };
    loadCalendarData();
  }, [selectedMonth]);

  // Добавьте эффект для отслеживания позиции курсора
  useEffect(() => {
    const handleMouseMove = (e) => {
      document.documentElement.style.setProperty("--mouse-x", `${e.clientX}px`);
      document.documentElement.style.setProperty("--mouse-y", `${e.clientY}px`);
    };

    document.addEventListener("mousemove", handleMouseMove);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  const generateTableHeaders = () => {
    const daysInMonth = new Date(
      selectedMonth.getFullYear(),
      selectedMonth.getMonth() + 1,
      0,
    ).getDate();

    return Array.from({ length: daysInMonth }, (_, i) => {
      const currentDate = new Date(
        Date.UTC(
          selectedMonth.getFullYear(),
          selectedMonth.getMonth(),
          i + 1,
          12,
          0,
          0,
          0,
        ),
      );

      const dateString = currentDate.toISOString().split("T")[0];
      const dayData = calendarData[dateString];

      return {
        day: i + 1,
        isWeekend: dayData ? !dayData.workday_flag : false,
        eventName: dayData?.event_name,
      };
    });
  };

  const formatTime = (hours) => formatHoursWithRounding(hours);

  const timesheetCellHelpers = createTimesheetCellHelpers(formatTime);
  const {
    classifyDayReports,
    getDayCellClassName,
    calculateReportTotals,
    buildDayCellTooltip,
  } = timesheetCellHelpers;

  const formatEmployeeName = (surname, name) => {
    if (!name) return surname;
    return `${surname} ${name.charAt(0)}.`;
  };

  const isReportInSelectedMonth = (report, month) => {
    const reportDate = new Date(report.fact_date_start);
    return (
      reportDate.getMonth() === month.getMonth() &&
      reportDate.getFullYear() === month.getFullYear()
    );
  };

  const getOtherDepartmentReportGroups = (userId) => {
    const otherReports = allVisibleReports.filter(
      (report) =>
        report.user?.id === userId &&
        report?.group?.id &&
        report.group.id !== selectedDepartment?.id &&
        isReportInSelectedMonth(report, selectedMonth),
    );

    const groupedByDepartment = otherReports.reduce((acc, report) => {
      const groupId = report.group.id;
      if (!acc[groupId]) {
        acc[groupId] = {
          group: report.group,
          reports: [],
        };
      }
      acc[groupId].reports.push(report);
      return acc;
    }, {});

    return Object.values(groupedByDepartment).sort((a, b) =>
      (a.group.name || "").localeCompare(b.group.name || "", "ru"),
    );
  };

  const renderOtherDepartmentRow = (
    user,
    { group, reports: groupReports },
    daysInMonth,
  ) => {
    const { workingTotal, overtimeTotal, dayOffTotal } =
      calculateReportTotals(groupReports);

    return (
      <tr
        key={`${user.id}-other-${group.id}`}
        className="timesheet-other-department-row"
      >
        <td
          className="timesheet-other-department-row__label"
          title={group.name}
        >
          ↳ {group.name}
        </td>
        {daysInMonth.map((dayData) => {
          const dayReports = groupReports.filter((report) => {
            const reportDate = new Date(report.fact_date_start);
            return reportDate.getDate() === dayData.day;
          });
          const classification = classifyDayReports(
            dayReports,
            dayData.isWeekend,
          );
          const statusClassName = getDayCellClassName(classification, dayData);

          return (
            <td
              key={dayData.day}
              className={`${statusClassName}${hasTimesheetStatusColoring(statusClassName) ? "" : " timesheet-other-department-row__default"}`}
            >
              <TimesheetDayCellContent
                classification={classification}
                formatTime={formatTime}
              />
            </td>
          );
        })}
        <td className="timesheet-other-department-row__default">
          {workingTotal > 0 ? formatTime(workingTotal) : ""}
        </td>
        <td
          className={
            overtimeTotal > 0
              ? "overtime-cell_true"
              : "timesheet-other-department-row__default"
          }
        >
          {overtimeTotal > 0 ? formatTime(overtimeTotal) : ""}
        </td>
        <td
          className={
            dayOffTotal > 0
              ? "dayoff-cell_true"
              : "timesheet-other-department-row__default"
          }
        >
          {dayOffTotal > 0 ? formatTime(dayOffTotal) : ""}
        </td>
      </tr>
    );
  };

  const renderAggregatedDepartmentRow = (
    user,
    daysInMonth,
    userReports,
    otherDepartmentGroups,
    {
      rowKey,
      rowClassName = "timesheet-total-department-row",
      labelClassName = "timesheet-total-department-row__label",
      labelContent,
    },
  ) => {
    const allUserReports = allVisibleReports.filter(
      (report) =>
        report.user?.id === user.id &&
        isReportInSelectedMonth(report, selectedMonth),
    );

    const mainTotals = calculateReportTotals(userReports);
    const otherTotals = otherDepartmentGroups.map((groupData) =>
      calculateReportTotals(groupData.reports),
    );
    const workingTotal =
      mainTotals.workingTotal +
      otherTotals.reduce((sum, totals) => sum + totals.workingTotal, 0);
    const overtimeTotal =
      mainTotals.overtimeTotal +
      otherTotals.reduce((sum, totals) => sum + totals.overtimeTotal, 0);
    const dayOffTotal =
      mainTotals.dayOffTotal +
      otherTotals.reduce((sum, totals) => sum + totals.dayOffTotal, 0);

    return (
      <tr key={rowKey} className={rowClassName || undefined}>
        <td className={labelClassName || undefined}>{labelContent}</td>
        {daysInMonth.map((dayData) => {
          const dayReports = allUserReports.filter((report) => {
            const reportDate = new Date(report.fact_date_start);
            return reportDate.getDate() === dayData.day;
          });
          const classification = classifyDayReports(
            dayReports,
            dayData.isWeekend,
          );

          return (
            <td
              key={dayData.day}
              className={getDayCellClassName(classification, dayData)}
            >
              <TimesheetDayCellContent
                classification={classification}
                formatTime={formatTime}
              />
            </td>
          );
        })}
        <td className="total-hours">
          {workingTotal > 0 ? formatTime(workingTotal) : ""}
        </td>
        <td className={overtimeTotal > 0 ? "overtime-cell_true" : ""}>
          {overtimeTotal > 0 ? formatTime(overtimeTotal) : ""}
        </td>
        <td className={dayOffTotal > 0 ? "dayoff-cell_true" : ""}>
          {dayOffTotal > 0 ? formatTime(dayOffTotal) : ""}
        </td>
      </tr>
    );
  };

  const renderMultiDeptExpandButton = (userId, isExpanded) => (
    <button
      type="button"
      className={`timesheet-expand-btn${isExpanded ? " timesheet-expand-btn--expanded" : ""}`}
      onClick={() => toggleMultiDeptExpand(userId)}
      aria-expanded={isExpanded}
      aria-label={
        isExpanded ? "Скрыть разбивку по отделам" : "Показать разбивку по отделам"
      }
      title={
        isExpanded ? "Скрыть разбивку по отделам" : "Показать разбивку по отделам"
      }
    >
      {isExpanded ? "−" : "+"}
    </button>
  );

  const renderEmployeeNameCell = (user, showExpandButton, isExpanded) => (
    <span className="timesheet-name-cell">
      {showExpandButton && renderMultiDeptExpandButton(user.id, isExpanded)}
      {formatEmployeeName(user.surname, user.name)}
      {user.roles && user.roles.some((role) => /стаж[её]р/i.test(role)) && (
        <>
          <br />
          <span className="trainee-label">(Стажер)</span>
        </>
      )}
    </span>
  );

  const renderTimesheet = () => {
    const daysInMonth = generateTableHeaders();

    return (
      <div className="timesheet-table-container">
        <table className="timesheet-table">
          <thead>
            <tr>
              <th>ФИО</th>
              {daysInMonth.map((dayData) => (
                <th
                  key={dayData.day}
                  className={dayData.isWeekend ? "weekend" : ""}
                  data-tooltip={dayData.eventName || undefined}
                >
                  {dayData.day}
                </th>
              ))}
              <th className="rotated-header" data-content="Рабочие часы"></th>
              <th className="rotated-header" data-content="Переработки"></th>
              <th className="rotated-header" data-content="Отгулы"></th>
            </tr>
          </thead>
          <tbody>
            {[...departmentUsers]
              .sort((a, b) => {
                const surnameA = (a.surname || "").toLowerCase();
                const surnameB = (b.surname || "").toLowerCase();
                if (surnameA !== surnameB) {
                  return surnameA.localeCompare(surnameB, "ru");
                }
                const nameA = (a.name || "").toLowerCase();
                const nameB = (b.name || "").toLowerCase();
                return nameA.localeCompare(nameB, "ru");
              })
              .filter((user) => {
                const userReports = reports.filter((report) => {
                  const reportDate = new Date(report.fact_date_start);
                  return (
                    report.user.id === user.id &&
                    reportDate.getMonth() === selectedMonth.getMonth() &&
                    reportDate.getFullYear() === selectedMonth.getFullYear()
                  );
                });
                const otherDepartmentGroups = getOtherDepartmentReportGroups(
                  user.id,
                );
                const hasOtherDepartmentReports = otherDepartmentGroups.some(
                  (groupData) => groupData.reports.length > 0,
                );
                return userReports.length > 0 || hasOtherDepartmentReports;
              })
              .map((user) => {
                const userReports = reports.filter((report) => {
                  const reportDate = new Date(report.fact_date_start);
                  return (
                    report.user.id === user.id &&
                    reportDate.getMonth() === selectedMonth.getMonth() &&
                    reportDate.getFullYear() === selectedMonth.getFullYear()
                  );
                });

                const { overtimeTotal, dayOffTotal } =
                  calculateReportTotals(userReports);
                const otherDepartmentGroups = getOtherDepartmentReportGroups(
                  user.id,
                );
                const hasMultipleDepartments = otherDepartmentGroups.length > 0;
                const isMultiDeptExpanded = expandedMultiDeptUsers.has(user.id);

                if (hasMultipleDepartments) {
                  return (
                    <React.Fragment key={user.id}>
                      {renderAggregatedDepartmentRow(
                        user,
                        daysInMonth,
                        userReports,
                        otherDepartmentGroups,
                        {
                          rowKey: user.id,
                          rowClassName: "",
                          labelClassName: "",
                          labelContent: renderEmployeeNameCell(
                            user,
                            true,
                            isMultiDeptExpanded,
                          ),
                        },
                      )}
                      {isMultiDeptExpanded &&
                        [
                          {
                            group: selectedDepartment,
                            reports: userReports,
                          },
                          ...otherDepartmentGroups,
                        ].map((groupData) =>
                          renderOtherDepartmentRow(
                            user,
                            groupData,
                            daysInMonth,
                          ),
                        )}
                    </React.Fragment>
                  );
                }

                return (
                  <React.Fragment key={user.id}>
                    <tr>
                      <td>{renderEmployeeNameCell(user, false, false)}</td>
                      {daysInMonth.map((dayData) => {
                        const dayReports = userReports.filter((report) => {
                          const reportDate = new Date(report.fact_date_start);
                          return reportDate.getDate() === dayData.day;
                        });

                        const hasNotSelfEntered = dayReports.some(
                          (r) =>
                            r.self_entered === false &&
                            !TIMESHEET_SPECIAL_JOB_TYPES[
                              r.portal_job_type?.id
                            ] &&
                            !r.is_day_off &&
                            !TIMESHEET_BUSINESS_TRIP_IDS.includes(
                              r.portal_job_type?.id,
                            ),
                        );

                        const classification = classifyDayReports(
                          dayReports,
                          dayData.isWeekend,
                        );
                        const tooltipText = buildDayCellTooltip(
                          dayData,
                          classification,
                        );

                        return (
                          <td
                            key={dayData.day}
                            className={getDayCellClassName(
                              classification,
                              dayData,
                            )}
                            data-tooltip={tooltipText}
                          >
                            <span>
                              <TimesheetDayCellContent
                                classification={classification}
                                formatTime={formatTime}
                                hasNotSelfEntered={hasNotSelfEntered}
                              />
                            </span>
                          </td>
                        );
                      })}
                      <td className="total-hours">
                        {formatTime(
                          calculateReportTotals(userReports).workingTotal,
                        )}
                      </td>
                      <td
                        className={
                          overtimeTotal > 0 ? "overtime-cell_true" : ""
                        }
                      >
                        {overtimeTotal > 0 ? formatTime(overtimeTotal) : ""}
                      </td>
                      <td
                        className={dayOffTotal > 0 ? "dayoff-cell_true" : ""}
                      >
                        {dayOffTotal > 0 ? formatTime(dayOffTotal) : ""}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
          </tbody>
        </table>
        {isLoadingTable && (
          <div className="table-loader-overlay">
            <div className="loader-content">
              <Preloader />
            </div>
          </div>
        )}
      </div>
    );
  };

  // Функции для работы с запросами на согласование

  const showNotification = (type, title, message) => {
    setNotification({
      isVisible: true,
      type,
      title,
      message,
    });
  };

  const hideNotification = () => {
    setNotification((prev) => ({
      ...prev,
      isVisible: false,
    }));
  };

  const handleApprovalRequestClick = (request) => {
    console.log("Открытие запроса на согласование:", request);

    const department = availableDepartments.find(
      (dept) => dept.id === request.departmentId,
    );
    if (!department) {
      console.error("Отдел не найден:", request.departmentId);
      return;
    }

    const [year, month] = request.month.split("-");
    const monthDate = new Date(parseInt(year), parseInt(month) - 1, 1);

    setSelectedDepartment(department);
    setSelectedMonth(monthDate);
    setSelectedApprovalRequest(request);

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Функция для согласования табеля
  const handleApproveTimesheet = async () => {
    if (!selectedApprovalRequest) return;

    try {
      console.log("Согласование табеля:", selectedApprovalRequest);

      // Находим шаг согласования для текущего пользователя
      const currentUserStep = selectedApprovalRequest.originalData?.steps?.find(
        (step) => step.approver.id === userData.id,
      );

      if (!currentUserStep) {
        console.error("Не найден шаг согласования для текущего пользователя");
        return;
      }

      // Обновляем шаг согласования через API
      await updateTimesheetStep(currentUserStep.id, {
        status: TIMESHEET_STATUSES.APPROVED,
      });

      // Перезагружаем список запросов на согласование
      await loadApprovalRequests();

      // Обновляем счетчик табелей
      if (userData?.id) {
        updatePendingCount(userData.id);
      }

      // Очищаем выбранный запрос
      setSelectedApprovalRequest(null);

      // Показываем уведомление об успехе
      showNotification(
        "success",
        "Табель согласован",
        "Табель успешно согласован",
      );

      console.log("Табель успешно согласован");
    } catch (error) {
      console.error("Ошибка при согласовании табеля:", error);
      showNotification(
        "error",
        "Ошибка согласования",
        error.message || "Ошибка при согласовании табеля. Попробуйте еще раз.",
      );
    }
  };

  // Функция для отклонения табеля
  const handleRejectTimesheet = async () => {
    if (!selectedApprovalRequest) return;

    try {
      console.log("Отклонение табеля:", selectedApprovalRequest);

      // Находим шаг согласования для текущего пользователя
      const currentUserStep = selectedApprovalRequest.originalData?.steps?.find(
        (step) => step.approver.id === userData.id,
      );

      if (!currentUserStep) {
        console.error("Не найден шаг согласования для текущего пользователя");
        return;
      }

      // Обновляем шаг согласования через API
      const result = await updateTimesheetStep(currentUserStep.id, {
        status: TIMESHEET_STATUSES.REJECTED,
      });

      // Перезагружаем список запросов на согласование
      await loadApprovalRequests();

      // Обновляем счетчик табелей
      if (userData?.id) {
        updatePendingCount(userData.id);
      }

      // Очищаем выбранный запрос
      setSelectedApprovalRequest(null);

      // Показываем уведомление об успехе
      if (result.deleted) {
        showNotification(
          "success",
          "Табель отклонен",
          "Табель отклонен и согласование прекращено",
        );
      } else {
        showNotification(
          "success",
          "Табель отклонен",
          "Табель успешно отклонен",
        );
      }

      console.log("Табель успешно отклонен");
    } catch (error) {
      console.error("Ошибка при отклонении табеля:", error);
      showNotification(
        "error",
        "Ошибка отклонения",
        error.message || "Ошибка при отклонении табеля. Попробуйте еще раз.",
      );
    }
  };

  if (!canViewApprovalRequests()) {
    return (
      <div className="timesheet-approval">
        <div className="access-denied">
          <h2>Доступ запрещен</h2>
          <p>У вас нет прав для просмотра запросов на согласование табелей.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="timesheet-approval">
      <h2 className="timesheet-approval__title">Согласование табелей</h2>

      {isLoadingDepartments ? (
        <div className="timesheet-approval__loader">
          <Preloader />
        </div>
      ) : (
        <>
          {/* Секция запросов на согласование табелей */}
          <div className="approval-requests">
            <div className="approval-requests__header">
              <h3 className="approval-requests__title">
                Запросы на согласование табелей
              </h3>

              {/* Фильтры */}
              <div className="approval-requests__filters">
                <div className="approval-requests__filter-group">
                  <label className="approval-requests__filter-label">
                    Отдел:
                  </label>
                  <select
                    className="approval-requests__filter-select"
                    value={departmentFilter}
                    onChange={handleDepartmentFilterChange}
                  >
                    <option value="">Все отделы</option>
                    {getUniqueDepartments().map((department) => (
                      <option key={department} value={department}>
                        {department}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="approval-requests__filter-group">
                  <label className="approval-requests__filter-label">
                    Период:
                  </label>
                  <select
                    className="approval-requests__filter-select"
                    value={periodFilter}
                    onChange={handlePeriodFilterChange}
                  >
                    <option value="">Все периоды</option>
                    {getUniquePeriods().map((period) => (
                      <option key={period} value={period}>
                        {period}
                      </option>
                    ))}
                  </select>
                </div>

                {(departmentFilter || periodFilter) && (
                  <button
                    className="approval-requests__clear-filters-btn"
                    onClick={clearFilters}
                    type="button"
                  >
                    Очистить фильтры
                  </button>
                )}
              </div>
            </div>

            <div className="approval-requests__table-container">
              <table className="approval-requests__table">
                <thead>
                  <tr>
                    <th>Отдел</th>
                    <th>Отправитель</th>
                    <th>Период</th>
                    <th>Статус</th>
                    <th>Дата отправки</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredApprovals().map((request) => (
                    <tr
                      key={request.id}
                      className={`approval-requests__row ${
                        selectedApprovalRequest &&
                        selectedApprovalRequest.id === request.id
                          ? "approval-requests__row--selected"
                          : ""
                      }`}
                      onClick={() => handleApprovalRequestClick(request)}
                    >
                      <td className="approval-requests__department">
                        {request.department}
                      </td>
                      <td className="approval-requests__sender">
                        {request.submittedBy}
                      </td>
                      <td className="approval-requests__date">
                        {request.date}
                      </td>
                      <td className="approval-requests__status">
                        <div className="approval-requests__approvers">
                          {request.approvers?.map((approver, index) => (
                            <div
                              key={index}
                              className={`approval-requests__approver approval-requests__approver--${approver.status}`}
                            >
                              <span className="approval-requests__approver-name">
                                {approver.name}:
                              </span>
                              <span
                                className={`approval-requests__approver-status approval-requests__approver-status--${
                                  approver.status
                                } ${getStatusColor(approver.status)}`}
                              >
                                {getStatusText(approver.status)}
                              </span>
                            </div>
                          )) || (
                            <div className="approval-requests__approver approval-requests__approver--pending">
                              <span
                                className={`approval-requests__approver-status approval-requests__approver-status--pending ${getStatusColor(
                                  request.status,
                                )}`}
                              >
                                {getStatusText(request.status)}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="approval-requests__submission-date">
                        {request.submissionDate}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {getFilteredApprovals().length === 0 && (
              <div className="approval-requests__empty">
                <p>
                  {pendingApprovals.length === 0
                    ? "Нет запросов на согласование"
                    : "Нет запросов, соответствующих выбранным фильтрам"}
                </p>
              </div>
            )}
          </div>

          {/* Отображение табеля для выбранного запроса */}
          {selectedApprovalRequest && selectedDepartment && (
            <div className="timesheet-view">
              {isLoading ? (
                <div className="timesheet-approval__loader">
                  <Preloader />
                </div>
              ) : (
                <>
                  <div className="timesheet-view__header">
                    <h3 className="timesheet-view__title">
                      Табель отдела: {selectedDepartment.name}
                    </h3>
                    <div className="timesheet-view__info">
                      <div className="timesheet-view__info-item">
                        <span className="timesheet-view__info-label">
                          Отдел:
                        </span>
                        <span className="timesheet-view__info-value">
                          {selectedDepartment.name}
                        </span>
                      </div>
                      <div className="timesheet-view__info-item">
                        <span className="timesheet-view__info-label">
                          Период:
                        </span>
                        <span className="timesheet-view__info-value">
                          {selectedMonth.toLocaleDateString("ru", {
                            month: "long",
                            year: "numeric",
                          })}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="report-status-legend">
                    <div className="legend-title">Статус отчета:</div>
                    <div className="legend-items">
                      <div className="legend-item">
                        <div className="legend-color regular-report"></div>
                        <span>Обычный отчет</span>
                      </div>
                      <div className="legend-item">
                        <div className="legend-color overtime-cell_true"></div>
                        <span>Переработка</span>
                      </div>
                      <div className="legend-item">
                        <div className="legend-color dayoff-cell_true"></div>
                        <span>Отгул</span>
                      </div>
                      <div className="legend-item">
                        <div className="legend-color bl"></div>
                        <span className="sick-leave">БЛ - Больничный</span>
                      </div>
                      <div className="legend-item">
                        <div className="legend-color ot"></div>
                        <span className="vacation">ОТ - Отпуск</span>
                      </div>
                      <div className="legend-item">
                        <div className="legend-color bs"></div>
                        <span className="unpaid-leave">
                          БС - Отпуск без содержания
                        </span>
                      </div>
                      <div className="legend-item">
                        <div className="legend-color business-trip"></div>
                        <span>Командировка</span>
                      </div>
                      <div className="legend-item">
                        <span
                          className="not-self-entered"
                          style={{ fontWeight: "bold", color: "#d32f2f" }}
                        >
                          часы
                        </span>
                        <span>— отчет создан администратором</span>
                      </div>
                    </div>
                  </div>

                  {renderTimesheet()}

                  {/* Кнопки согласования/отклонения табеля */}
                  {(() => {
                    // Проверяем, есть ли у текущего пользователя активный шаг согласования
                    const currentUserStep =
                      selectedApprovalRequest.originalData?.steps?.find(
                        (step) =>
                          step.approver.id === userData.id &&
                          step.status === TIMESHEET_STATUSES.PENDING,
                      );

                    // Показываем кнопки только если есть активный шаг согласования
                    if (!currentUserStep) {
                      return null;
                    }

                    return (
                      <div className="timesheet-approval-actions">
                        <div className="timesheet-approval-actions__header">
                          <h4 className="timesheet-approval-actions__title">
                            Согласование табеля:{" "}
                            {selectedApprovalRequest.department}
                          </h4>
                          <p className="timesheet-approval-actions__subtitle">
                            Период: {selectedApprovalRequest.date} |
                            Отправитель: {selectedApprovalRequest.submittedBy}
                          </p>
                        </div>
                        <div className="timesheet-approval-actions__buttons">
                          <button
                            className="timesheet-approval-actions__approve-btn"
                            onClick={handleApproveTimesheet}
                          >
                            Согласовать
                          </button>
                          <button
                            className="timesheet-approval-actions__reject-btn"
                            onClick={handleRejectTimesheet}
                          >
                            Отклонить
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* Компонент уведомлений */}
      <TimesheetNotification
        isVisible={notification.isVisible}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        onClose={hideNotification}
      />
    </div>
  );
};

export default TimesheetApproval;
